/**
 * «Surtido» — the platform's codes in words (ADR 9058, 9060, ADR 9070 R3): the plan's own vocabulary — peer bases, hierarchy levels,
 * actions, flags, decisions, measures, plan states. A code this build has no words for reads as the code itself (`codeWords`), never as
 * a blank and never invented.
 *
 * **Reason codes are not here.** The words for why a line, a gap, a rule, a flag, a placement, a refusal or a decision are the
 * platform's (`GET /assortment/reason-labels`, read by `./reasons.ts`); this build keeps no copy of them.
 *
 * One literal per key, so `npm run i18n:coverage` sees every word as used.
 */

import { codeWords, type Translate } from '@/planning/words';

/** The peer bases. The cluster ones are offered while no scheme is accepted, and say so wherever they are used. */
export const BASIS_KEY: Readonly<Record<string, string>> = {
  volume_band: 'preseason.assortment.basis.volume_band',
  cluster: 'preseason.assortment.basis.cluster',
  cluster_and_volume_band: 'preseason.assortment.basis.cluster_and_volume_band',
  area_band: 'preseason.assortment.basis.area_band',
};

/** The hierarchy levels. */
export const LEVEL_KEY: Readonly<Record<string, string>> = {
  major_category: 'preseason.assortment.level.major_category',
  category: 'preseason.assortment.level.category',
  sub_category: 'preseason.assortment.level.sub_category',
};

export const ACTION_KEY: Readonly<Record<string, string>> = {
  add_now: 'preseason.assortment.action.add_now',
  add_next_buy: 'preseason.assortment.action.add_next_buy',
  remove: 'preseason.assortment.action.remove',
  keep: 'preseason.assortment.action.keep',
};

/** An action in one short word — the list of lines has a narrow column for it. */
export const ACTION_SHORT_KEY: Readonly<Record<string, string>> = {
  add_now: 'preseason.assortment.actionShort.add_now',
  add_next_buy: 'preseason.assortment.actionShort.add_next_buy',
  remove: 'preseason.assortment.actionShort.remove',
  keep: 'preseason.assortment.actionShort.keep',
};

/** The sentence a line's explanation opens with, by action. */
export const WHY_SENTENCE_KEY: Readonly<Record<string, string>> = {
  add_now: 'preseason.assortment.why.sentence.add_now',
  add_next_buy: 'preseason.assortment.why.sentence.add_next_buy',
  remove: 'preseason.assortment.why.sentence.remove',
  keep: 'preseason.assortment.why.sentence.keep',
};

export const FLAG_KEY: Readonly<Record<string, string>> = {
  under_assorted: 'preseason.assortment.flag.under_assorted',
  over_assorted: 'preseason.assortment.flag.over_assorted',
  missing: 'preseason.assortment.flag.missing',
  bounded: 'preseason.assortment.flag.bounded',
  refused: 'preseason.assortment.flag.refused',
};

export const DECISION_KEY: Readonly<Record<string, string>> = {
  accept: 'preseason.assortment.decision.accept',
  reject: 'preseason.assortment.decision.reject',
  undecided: 'preseason.assortment.decision.undecided',
};

/** The measures the matrix draws, one at a time. */
export const MEASURE_NAME_KEY: Readonly<Record<string, string>> = {
  models: 'preseason.assortment.measure.models',
  salesPerModel: 'preseason.assortment.measure.salesPerModel',
  sales: 'preseason.assortment.measure.sales',
  units: 'preseason.assortment.measure.units',
  margin: 'preseason.assortment.measure.margin',
  carrying: 'preseason.assortment.measure.carrying',
  under: 'preseason.assortment.measure.under',
  over: 'preseason.assortment.measure.over',
  missing: 'preseason.assortment.measure.missing',
  tail: 'preseason.assortment.measure.tail',
  addNow: 'preseason.assortment.measure.addNow',
  remove: 'preseason.assortment.measure.remove',
};

/** What a band is cut on: the pairs a store sold in the window, or its selling area. */
export const BAND_MEASURE_KEY: Readonly<Record<string, string>> = {
  pairs_sold: 'preseason.assortment.measureName.pairs_sold',
  selling_area: 'preseason.assortment.measureName.selling_area',
};

export const PLAN_STATE_KEY: Readonly<Record<string, string>> = {
  queued: 'preseason.assortment.planState.queued',
  running: 'preseason.assortment.planState.running',
  failed: 'preseason.assortment.planState.failed',
  completed: 'preseason.assortment.planState.completed',
  refused: 'preseason.assortment.planState.refused',
  expired: 'preseason.assortment.planState.expired',
};

/** The words of a code in one of the maps above, or the code where the build has none. */
export function words(t: Translate, map: Readonly<Record<string, string>>, code: string | null | undefined): string {
  return code === null || code === undefined || code === '' ? '' : codeWords(t, map, code);
}
